import { api } from "../api";
import { canWrite, readOnlyNotice } from "../utils/permissions";
import { showToast } from "../utils/toast";
import type { SchoolSettings } from "../types";
import { esc } from "../utils/html";

/**
 * Optionen des Stundenplans.
 *
 * Die unterrichtsfreien Zeiten liegen bewusst nicht hier, sondern im Stundenplan unter
 * "Verwalten": dort werden sie beim Planen gebraucht, so wie die Ferien in der
 * Praktikumsplanung.
 */

let settings: SchoolSettings | null = null;

/** Schalter mit sofortigem Speichern, wie in den übrigen Einstellungen. */
function toggleRow(id: string, label: string, hint: string, checked: boolean): string {
  return `
    <div style="padding:.75rem 0;border-bottom:1px solid var(--gray-100)">
      <label class="checkbox-label">
        <input type="checkbox" id="${id}" ${checked ? "checked" : ""} ${canWrite() ? "" : "disabled"} />
        <span>
          <span style="font-weight:500">${label}</span>
          ${hint ? `<span style="display:block;font-size:.8125rem;color:var(--gray-500)">${hint}</span>` : ""}
        </span>
      </label>
    </div>`;
}

export async function render(el: HTMLElement) {
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  try {
    settings = await api.settings.get();
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }

  const vorplanung = settings?.stundenplanVorplanungEnabled === true;

  el.innerHTML = `
    <div class="settings-page-header">
      <div>
        <h1>Optionen</h1>
        <p>Wie der Stundenplan mit Praktikumszeiten und noch nicht veröffentlichten Terminen umgeht. Ferien werden im Stundenplan unter „Verwalten" gepflegt.</p>
      </div>
    </div>
    ${readOnlyNotice()}

    ${settings?.planungEnabled ? `
    <div class="card" style="padding:1.25rem;margin-bottom:1.5rem">
      <h2 style="font-size:1rem;font-weight:600;margin:0 0 .25rem">Praktikum</h2>
      ${toggleRow("o-practice", "Praktikumswochen auslassen",
        "Wochen überspringen, in denen die ganze Klasse im Praktikum ist. Ist auch nur ein Schüler da, findet Unterricht statt.",
        settings?.stundenplanSkipPractice !== false)}
    </div>` : ""}

    <div class="card" style="padding:1.25rem">
      <h2 style="font-size:1rem;font-weight:600;margin:0 0 .25rem">Vorplanung</h2>
      <p style="font-size:.8125rem;color:var(--gray-500);margin:0 0 .75rem">
        Wie in der Praktikumsplanung, aber mit eigenem Schalter: Termine lassen sich
        planen, bevor sie für Schüler sichtbar sind, und getrennt davon als vorläufig
        kennzeichnen.
      </p>
      ${toggleRow("o-vorplanung", "Vorplanung im Stundenplan",
        "Blendet die Kennzeichen sichtbar und vorläufig am Unterricht ein.", vorplanung)}
      <div id="o-defaults" class="${vorplanung ? "" : "hidden"}" style="padding-left:1.75rem">
        <div style="font-size:.8125rem;font-weight:600;color:var(--gray-700);padding-top:.75rem">
          Standard für neu angelegte Termine
        </div>
        <div style="font-size:.8125rem;color:var(--gray-500);margin-top:.125rem">
          Zwei unabhängige Kennzeichen: ein Termin kann sichtbar und trotzdem vorläufig
          sein. Am einzelnen Termin ist beides änderbar.
        </div>
        ${toggleRow("o-default-visible", "Für Schüler sichtbar", "",
          settings?.stundenplanDefaultVisible === true)}
        ${toggleRow("o-default-preliminary", "Als vorläufig gekennzeichnet", "",
          settings?.stundenplanDefaultPreliminary === true)}
        <div class="alert alert-info" id="o-defaults-preview" style="font-size:.8125rem;margin:.75rem 0 0"></div>
      </div>
    </div>`;

  if (!canWrite()) return;

  const checkbox = (id: string) => el.querySelector<HTMLInputElement>(`#${id}`);

  // Ohne Praktikumsplanung gibt es den Schalter nicht.
  checkbox("o-practice")?.addEventListener("change", async (e) => {
    const input = e.target as HTMLInputElement;
    try {
      await api.settings.stundenplanSkipPractice(input.checked);
      showToast("Gespeichert");
    } catch (err: any) {
      input.checked = !input.checked;
      showToast("Fehler: " + err.message);
    }
  });

  // Macht die Unabhaengigkeit der beiden Kennzeichen greifbar: die Zeile nennt das
  // Ergebnis beider Schalter zusammen.
  const updatePreview = () => {
    const box = el.querySelector("#o-defaults-preview");
    if (!box) return;
    const visible = checkbox("o-default-visible")?.checked;
    const preliminary = checkbox("o-default-preliminary")?.checked;
    box.textContent = "Ein neu angelegter Termin ist damit "
      + (visible ? "für Schüler sichtbar" : "für Schüler noch nicht sichtbar")
      + (preliminary ? " und als vorläufig gekennzeichnet." : " und gilt als bestätigt.");
  };
  updatePreview();

  const saveVorplanung = async (input: HTMLInputElement) => {
    try {
      await api.settings.stundenplanVorplanung(
        checkbox("o-vorplanung")!.checked,
        checkbox("o-default-visible")!.checked,
        checkbox("o-default-preliminary")!.checked,
      );
      showToast("Gespeichert");
    } catch (err: any) {
      input.checked = !input.checked;
      showToast("Fehler: " + err.message);
    }
    updatePreview();
  };

  checkbox("o-vorplanung")!.addEventListener("change", async (e) => {
    const input = e.target as HTMLInputElement;
    el.querySelector("#o-defaults")!.classList.toggle("hidden", !input.checked);
    await saveVorplanung(input);
  });
  checkbox("o-default-visible")!.addEventListener("change", (e) => saveVorplanung(e.target as HTMLInputElement));
  checkbox("o-default-preliminary")!.addEventListener("change", (e) => saveVorplanung(e.target as HTMLInputElement));
}
